import { useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { OrganizationAccessException } from "go-core/api/exceptions";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { ClientGrantsCreateModalProps, CreateOrganizationClientGrantApi } from "../../../services/types";

interface Props extends ClientGrantsCreateModalProps {
	form: UseFormReturn<CreateOrganizationClientGrantApi>;
}

const ClientGrantFormModal = ({
	defaultValues,
	onRefresh,
	onHide,
	updateClientGrant,
	createClientGrant,
	form,
}: Props) => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation("lib");
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		register,
		handleSubmit,
		watch,
		setError,
		formState: { errors },
		reset,
	} = form;

	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const requiredInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		try {
			setLoading(true);
			const isEdit = Boolean(defaultValues);
			if (isEdit && defaultValues?.id) {
				await updateClientGrant(defaultValues.id, data);
			} else {
				const clientGrant = await createClientGrant(data);
				reset(clientGrant);
			}
			await onRefresh();
			addSuccessFlash(t("common.flash.saved"));

			if (isEdit) {
				onHide();
			}
		} catch (err) {
			let newError: OrganizationAccessException | null = null;

			if (err instanceof OrganizationAccessException) {
				newError = new OrganizationAccessException({
					errors: err.errors.map((error) => ({ ...error, value: error.value ?? "0" })),
				});
			}

			handleError.alert(newError ?? err, addFlash);
		} finally {
			setLoading(false);
		}
	});

	return (
		<>
			<Modal.Header closeButton>
				<Modal.Title>
					{defaultValues
						? t("go_component.client_grants.header.edit_key.title")
						: t("go_component.client_grants.header.new_key.title")}
				</Modal.Title>
			</Modal.Header>
			<Form onSubmit={onSubmit}>
				<Modal.Body>
					<FormInput
						name="label"
						errors={errors}
						register={register}
						label={t("go_component.client_grants.field.label.title")}
						customValidationConfig={requiredInputValidationConfig}
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit">
						{defaultValues ? t("common.action.save") : t("common.action.create")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</Form>
		</>
	);
};

export default ClientGrantFormModal;
